import {RosterData} from '@/types/types';
import {RosterRankSet, Title, ResourceRosterRank} from '@cquential/api';
import {PlanbordResource} from '@/types/api-object-extensions';
import {MoveTitleRankData, MoveResourceRankData, RosterRankItemWithRank} from '@/types';
import {ResourceRosterRankUtil} from '@/util/ResourceRosterRankUtil';

export class RosterRankSetManager {
    public static getLast(rosterRankSets: RosterRankSet[]): RosterRankSet {
        return rosterRankSets.sort((a: RosterRankSet, b: RosterRankSet): number => b.beginDate.getTime() - a.beginDate.getTime())[0];
    }

    public static applyRankSetToRoster(roster: RosterData, rosterRankSet: RosterRankSet): void {
        if (roster.rosterId !== rosterRankSet.rosterId) {
            throw new Error('Cannot update ranks with rankSet of other roster');
        }

        if (!rosterRankSet.titles || !rosterRankSet.resourceRosterRanks) {
            throw new Error('Cannot update ranks when titles or resource roster ranks are missing.');
        }

        roster.rosterRankSet = rosterRankSet;
        roster.titles = rosterRankSet.titles.sort((a: Title, b: Title): number => a.rankInRoster - b.rankInRoster);

        const rankPerResource: Record<number, number> = {};
        for (const resourceRosterRank of rosterRankSet.resourceRosterRanks) {
            rankPerResource[resourceRosterRank.resourceId] = resourceRosterRank.rank;
        }

        for (const resource of roster.rosterResources) {
            resource.rankInRoster = rankPerResource[resource.id!] ?? Infinity;
        }
        roster.rosterResources.sort((a: PlanbordResource, b: PlanbordResource): number => a.rankInRoster - b.rankInRoster);
    }

    public static rankSetWithUpdatedRanks(roster: RosterData, draggedItem: MoveTitleRankData | MoveResourceRankData): RosterRankSet {
        const sortedItems: RosterRankItemWithRank[] = RosterRankSetManager.getCurrentRanksFromRoster(roster);

        const draggedItemId: number = ResourceRosterRankUtil.isMoveResourceRankData(draggedItem) ? draggedItem.resourceId : draggedItem.titleId;
        const draggedItemType: RosterRankItemWithRank['type'] = ResourceRosterRankUtil.isMoveResourceRankData(draggedItem) ? 'resource' : 'title';
        const draggedItemIndex: number = sortedItems.findIndex((item: RosterRankItemWithRank): boolean => item.type === draggedItemType && item.id === draggedItemId);

        let draggedRankItem: RosterRankItemWithRank;
        if (draggedItemIndex === -1) {
            draggedRankItem = {type: draggedItemType, id: draggedItemId, rank: draggedItem.newRank!};
        } else {
            draggedRankItem = sortedItems[draggedItemIndex];
            sortedItems.splice(draggedItemIndex, 1); // remove item that was dragged from the list, we'll add it back as if it was new
        }

        const insertIndex: number = RosterRankSetManager.findInsertIndex(sortedItems, draggedItem.newRank!, draggedItem.currentRank);
        sortedItems.splice(insertIndex, 0, draggedRankItem);

        const rankByKey: Record<RosterRankItemWithRank['type'], Record<number, number>> = {resource: {}, title: {}};
        for (let i: number = 0; i < sortedItems.length; i++) {
            rankByKey[sortedItems[i].type][sortedItems[i].id] = i;
        }

        const updatedResourceRosterRanks: ResourceRosterRank[] = roster.rosterRankSet.resourceRosterRanks!.map((resourceRosterRank: ResourceRosterRank): ResourceRosterRank => {
            const rank: number | undefined = rankByKey['resource'][resourceRosterRank.resourceId];

            return rank !== undefined ? {...resourceRosterRank, rank} : resourceRosterRank;
        });

        if (ResourceRosterRankUtil.isMoveResourceRankData(draggedItem) && draggedItemIndex === -1) {
            // only needed for resources, as titles always exist in the rank set
            updatedResourceRosterRanks.push({
                resourceId: draggedItem.resourceId,
                rosterRankSetId: roster.rosterRankSet.id!,
                rank: rankByKey['resource'][draggedItem.resourceId],
            });
        }

        return {
            ...roster.rosterRankSet,
            titles: roster.rosterRankSet.titles!.map((title: Title): Title => {
                return {...title, rankInRoster: rankByKey['title'][title.id]};
            }),
            resourceRosterRanks: updatedResourceRosterRanks,
        };
    }

    private static getCurrentRanksFromRoster(roster: RosterData): RosterRankItemWithRank[] {
        const rankedItems: RosterRankItemWithRank[] = [];

        if (roster.titles) {
            for (const title of roster.titles) {
                rankedItems.push({
                    type: 'title',
                    id: title.id,
                    rank: title.rankInRoster,
                });
            }
        }

        for (const resource of roster.rosterResources) {
            if (resource.rankInRoster !== Infinity && resource.id) {
                rankedItems.push({
                    type: 'resource',
                    id: resource.id,
                    rank: resource.rankInRoster,
                });
            }
        }

        rankedItems.sort((a: RosterRankItemWithRank, b: RosterRankItemWithRank): number => a.rank - b.rank);

        return rankedItems;
    }

    private static findInsertIndex(rankedItems: RosterRankItemWithRank[], newRank: number, currentRank: number): number {
        let insertIndex: number;

        if (newRank > currentRank) {
            insertIndex = rankedItems.findIndex((item: RosterRankItemWithRank): boolean => item.rank > newRank);
        } else {
            insertIndex = rankedItems.findIndex((item: RosterRankItemWithRank): boolean => item.rank >= newRank);
        }

        if (insertIndex === -1) {
            return rankedItems.length;
        }

        return insertIndex;
    }
}
